/**
 * Speakers API adapter for uploading and managing speakers
 *
 * Reason: Handles speaker CSV uploads and segment assignments
 */

import { useMutation, UseMutationResult, useQuery, UseQueryResult } from 'react-query';
import ApiV2 from './api-v2';

export interface UploadSpeakersResponse {
  processed: number;
  success: number;
  failed: number;
  totalMatchedSegments: number;
  results: {
    speaker: { name: string; email: string };
    userId?: string;
    created: boolean;
    matchedSegments: number;
    errors?: string[];
  }[];
}

export interface GetUserAssignedSegmentsResponse {
  segmentIds: string[];
  segments: SpeakerAssignedSegment[];
  projectName?: string;
  videoId?: string;
  filtered?: boolean;
}

export interface SpeakerAssignedSegment {
  id: string;
  name?: string;
  projectId: string;
  /** Segment cut start in seconds. Present when filterByUser=true. */
  startTime?: number;
  /** Segment cut end in seconds. Present when filterByUser=true. */
  endTime?: number;
  /** Signed URL for the rendered segment MP4 — speaker-only download/play. */
  videoUrl?: string;
  /** Video id to use for the transcript-slice endpoint (defaults to "original"). */
  videoId?: string;
  /**
   * Streamcontroller PG fragment id linked from DDB. Workbench uses this
   * to find the authoritative PG row when scoping fragments for speakers
   * (DDB cut times can drift from PG, so we anchor off the id).
   */
  fragmentId?: string;
}

export interface SpeakerTranscriptItem {
  start: number;
  end: number;
  text: string;
  type: string;
  alternatives: { confidence: string | number; content: string }[];
}

export interface SpeakerSegmentTranscriptResponse {
  projectId: string;
  segmentId: string;
  startTime: number;
  endTime?: number;
  items: SpeakerTranscriptItem[];
  transcript: string;
}

export interface SpeakersPreviewResponse {
  preview: {
    speaker: { name: string; email: string };
    matchedSegments: {
      segmentId: string;
      segmentName?: string;
      projectId: string;
      projectName?: string;
    }[];
    matchedCount: number;
  }[];
  totalMatchedSegments: number;
  totalSegmentsWithSpeaker?: number;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Previews which segments would match each speaker (without creating users)
 *
 * @reason Shows matching segments in upload dialog before upload
 */
export function usePreviewSpeakers(
  orgId?: string,
): UseMutationResult<SpeakersPreviewResponse, Error, File> {
  return useMutation(async (file: File) => {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await ApiV2.post<ApiResponse<SpeakersPreviewResponse>>(
      `/api/orgs/${orgId}/speakers/preview`,
      formData,
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to preview speakers');
    }

    return response.data;
  });
}

/**
 * Uploads a CSV file with speaker names and emails
 *
 * @reason Allows admins to bulk upload speakers and automatically match them to segments
 */
export function useUploadSpeakers(
  orgId?: string,
): UseMutationResult<UploadSpeakersResponse, Error, File> {
  return useMutation(async (file: File) => {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await ApiV2.post<ApiResponse<UploadSpeakersResponse>>(
      `/api/orgs/${orgId}/speakers/upload`,
      formData,
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to upload speakers');
    }

    return response.data;
  });
}

export type SpeakerInviteStatus =
  | 'invited'
  | 'linked'
  | 'different_org'
  | 'invalid_email'
  | 'auth0_not_configured'
  | 'failed';

export interface SpeakerInviteOutcome {
  email: string;
  status: SpeakerInviteStatus;
  userId?: string;
  errorMessage?: string;
}

export interface InviteSegmentSpeakersResponse {
  projectId: string;
  segmentId: string;
  emailsAttempted: number;
  outcomes: SpeakerInviteOutcome[];
  counts?: Record<SpeakerInviteStatus, number>;
}

/**
 * Sends invites to the speakers on a specific segment's runsheet row.
 * Creates (or links) an Auth0 user per speaker with an email, grants
 * access to just this segment, and emails them with project/segment context.
 */
export function useInviteSegmentSpeakers(): UseMutationResult<
  InviteSegmentSpeakersResponse,
  Error,
  { projectId: string; segmentId: string }
> {
  return useMutation(async ({ projectId, segmentId }) => {
    const response = await ApiV2.post<ApiResponse<InviteSegmentSpeakersResponse>>(
      `/api/projects/${projectId}/segments/${segmentId}/invite-speakers`,
      {},
    );
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to send invites');
    }
    return response.data;
  });
}

/**
 * Gets all segments assigned to the current user
 *
 * @reason Allows filtering segments on Workbench page for users with role "user"
 */
export function useGetUserAssignedSegments(
  projectId?: string,
): UseQueryResult<GetUserAssignedSegmentsResponse, Error> {
  return useQuery(
    ['user-assigned-segments', projectId],
    async () => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      const response = await ApiV2.get<ApiResponse<GetUserAssignedSegmentsResponse>>(
        `/api/projects/${projectId}/segments?filterByUser=true`,
      );

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to get assigned segments');
      }

      return response.data;
    },
    {
      enabled: !!projectId,
      retry: false, // Don't retry on failure
    },
  );
}

/**
 * Fetches the transcript items inside a single segment's cut window.
 * Backed by the eos-app endpoint that verifies the caller holds a
 * SpeakerSegmentMapping for (userId, projectId, segmentId).
 */
export function useGetSegmentTranscriptSlice(
  projectId?: string,
  segmentId?: string,
): UseQueryResult<SpeakerSegmentTranscriptResponse, Error> {
  return useQuery(
    ['speaker-segment-transcript', projectId, segmentId],
    async () => {
      if (!projectId || !segmentId) {
        throw new Error('projectId and segmentId are required');
      }
      const response = await ApiV2.get<ApiResponse<SpeakerSegmentTranscriptResponse>>(
        `/api/projects/${projectId}/segments/${segmentId}/transcript`,
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load transcript');
      }
      return response.data;
    },
    {
      enabled: !!projectId && !!segmentId,
      retry: false,
    },
  );
}
